'use client';

import { TmxProfiler } from '@workspace/lib/components/organisms/TmxProfiler';
import { resolveTmxProfilingConfig } from '@workspace/lib/utils/tmx-profiling';

import { APP_ROUTES } from '@/constants/application-routes';
import { usePathname } from '@/i18n/navigation';

/**
 * RO-specific allow-list of internal (de-localized, English) routes on which
 * TMX device profiling should run. These are the Address, Marketing Consents
 * (agreements) and Marital Status steps — the steps that immediately precede
 * the backend credit-scoring TMX Session Query, so profiling must have fired
 * against the shared `tmxSessionId` by the time those checks run.
 */
const TMX_PROFILING_ROUTES: readonly string[] = [
  APP_ROUTES.ADDRESS_DETAILS,
  APP_ROUTES.AGREEMENTS,
  APP_ROUTES.MARITAL_STATUS_DETAILS,
];

/**
 * Resolved once at module scope. The explicit static `process.env.NEXT_PUBLIC_*`
 * references below are required for Next.js to inline these values into the
 * client bundle — see the call-site comment for why.
 */
// Pass explicit static `process.env.NEXT_PUBLIC_*` references so Next.js
// inlines these values into the client bundle. Calling the resolver with no
// argument (dynamic `process.env` access inside the shared lib) would leave
// them undefined on the client and silently disable profiling (CST-25471 PR review).
const tmxConfig = resolveTmxProfilingConfig({
  NEXT_PUBLIC_TMX_PROFILING_ENABLED:
    process.env.NEXT_PUBLIC_TMX_PROFILING_ENABLED,
  NEXT_PUBLIC_TMX_ORG_ID: process.env.NEXT_PUBLIC_TMX_ORG_ID,
  NEXT_PUBLIC_TMX_PROFILING_DOMAIN:
    process.env.NEXT_PUBLIC_TMX_PROFILING_DOMAIN,
  NEXT_PUBLIC_TMX_PAGE_ID: process.env.NEXT_PUBLIC_TMX_PAGE_ID,
});

/**
 * TmxProfilerGate — RO gate for TMX device profiling.
 *
 * Activates the shared {@link TmxProfiler} only on the RO journey steps that
 * precede the backend credit-scoring TMX query (Address, Marketing Consents
 * and Marital Status), by matching the de-localized internal pathname from
 * `@/i18n/navigation` against {@link APP_ROUTES}. The route list is RO-specific
 * — PL has its own gate.
 */
export function TmxProfilerGate() {
  const pathname = usePathname();
  const active = TMX_PROFILING_ROUTES.includes(pathname);

  return <TmxProfiler config={tmxConfig} active={active} />;
}
